import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Alert, Box, Button, Grid, InputAdornment, Paper, Stack, Typography } from "@mui/material";
import { FormikProps } from "formik";

import { FormikDecimalTextField } from "../../../../../../../../formik/FormikDecimalTextField";
import { FormikTextField } from "../../../../../../../../formik/FormikTextField";
import { FormikYesNoAutocomplete } from "../../../../../../../../formik/FormikYesNoAutocomplete";
import { IOptionItem } from "../../../../../../../../formik/formik-autocomplete/FormikAutocomplete.types";
import { FormikAutocompleteSelect } from "../../../../../../../../formik/formik-autocomplete/FormikAutocompleteSelect";
import { FormikDatePicker } from "../../../../../../../../formik/formik-date-picker/FormikDatePicker";
import { AssetEncumbrance } from "../../../../../../../api/ClientFileApi";
import { FieldSpacer } from "../../../../../../components/field-spacer/FieldSpacer";
import { FieldWithInfo } from "../../../../../personal-data/components/field-with-info/FieldWithInfo";
import {
  PropertyRightField,
  PropertyRightsFormValues,
  PropertyRightsMaxLength,
} from "../../../../OtherPropertyRightsView.types";

const TAB = "clients.client.debtSettlement.assetInventory.propertyRightsTab";
const TENANCY = `${TAB}.tenancy`;
const FULL = 12;
const HALF = { xs: 12, md: 6 };

interface Props {
  formik: FormikProps<PropertyRightsFormValues>;
  index: number;
  number: number;
  claimOptions: IOptionItem[];
  onRemove: () => void;
}

export function TenancyRightBlock({ formik, index, number, claimOptions, onRemove }: Props) {
  const { t } = useTranslation();

  const right = formik.values.propertyRights[index];
  if (!right) {
    return null;
  }

  const isFurther = number > 1;
  const path = (field: PropertyRightField) => `propertyRights.${index}.${field}` as const;
  const fieldId = (field: PropertyRightField) => `property-rights-${right.key}-${field}`;
  const title = t(`${TENANCY}.blockTitle`, { number });

  const encumbranceOptions: IOptionItem[] = [
    { id: AssetEncumbrance.Yes, label: t(`${TENANCY}.encumbranceYes`) },
    { id: AssetEncumbrance.No, label: t(`${TENANCY}.encumbranceNo`) },
    { id: AssetEncumbrance.Unknown, label: t(`${TENANCY}.encumbranceUnknown`) },
  ];
  const selectPlaceholder = { placeholder: t(`${TAB}.pleaseSelect`) };

  const warning = (text: string) => (
    <Box component="span" sx={{ color: "error.main" }}>
      {text}
    </Box>
  );

  const hint = (text: string) => (
    <Grid size={FULL}>
      <Alert severity="info">
        {text} {t(`${TAB}.letter`)}
      </Alert>
      <FieldSpacer />
    </Grid>
  );

  const yesNo = (field: PropertyRightField, label: string, helperText?: ReactNode) => (
    <FormikYesNoAutocomplete
      id={fieldId(field)}
      formik={formik}
      path={path(field)}
      label={label}
      helperText={helperText}
      disableClearable
    />
  );

  const textArea = (field: PropertyRightField, label: string) => (
    <FormikTextField
      id={fieldId(field)}
      formik={formik}
      path={path(field)}
      label={label}
      multiline
      minRows={3}
      slotProps={{ input: { inputProps: { maxLength: PropertyRightsMaxLength.text } } }}
    />
  );

  const expectsProceedsLabel = t(isFurther ? `${TENANCY}.expectsProceedsFurther` : `${TENANCY}.expectsProceeds`);

  return (
    <Paper variant="outlined" component="article" aria-label={title} sx={{ p: 2 }}>
      <Stack gap={2}>
        <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <Grid container spacing={2}>
          <Grid size={FULL}>
            <FormikTextField
              id={fieldId("landlord")}
              formik={formik}
              path={path("landlord")}
              label={t(isFurther ? `${TENANCY}.landlordFurther` : `${TENANCY}.landlord`)}
              slotProps={{ input: { inputProps: { maxLength: PropertyRightsMaxLength.landlord } } }}
            />
          </Grid>
          <Grid size={FULL}>
            {yesNo("isCurrentHome", t(isFurther ? `${TENANCY}.isCurrentHomeFurther` : `${TENANCY}.isCurrentHome`))}
          </Grid>
          {right.isCurrentHome === false && (
            <Grid size={FULL}>{textArea("propertyDetails", t(`${TENANCY}.propertyDetails`))}</Grid>
          )}
          <Grid size={FULL}>
            <FieldWithInfo fieldLabel={expectsProceedsLabel} info={t(`${TENANCY}.expectsProceedsInfo`)}>
              {yesNo("expectsProceeds", expectsProceedsLabel)}
            </FieldWithInfo>
          </Grid>

          {right.expectsProceeds === true && (
            <>
              <Grid size={FULL}>
                <FormikAutocompleteSelect
                  id={fieldId("proceedsEncumbrance")}
                  formik={formik}
                  path={path("proceedsEncumbrance")}
                  label={t(`${TENANCY}.proceedsEncumbrance`)}
                  options={encumbranceOptions}
                  disableClearable
                  textFieldProps={selectPlaceholder}
                  helperText={
                    right.proceedsEncumbrance === AssetEncumbrance.Unknown
                      ? warning(t(`${TENANCY}.encumbranceWarning`))
                      : undefined
                  }
                />
              </Grid>
              {right.proceedsEncumbrance === AssetEncumbrance.Unknown && hint(t(`${TENANCY}.encumbranceHint`))}
              {right.proceedsEncumbrance === AssetEncumbrance.Yes && (
                <Grid size={FULL}>
                  <FormikAutocompleteSelect
                    id={fieldId("encumberedClaimId")}
                    formik={formik}
                    path={path("encumberedClaimId")}
                    label={t(`${TENANCY}.encumberedClaim`)}
                    options={claimOptions}
                    disableClearable
                    textFieldProps={selectPlaceholder}
                  />
                </Grid>
              )}

              <Grid size={FULL}>
                {yesNo(
                  "isProceedsAmountKnown",
                  t(`${TENANCY}.isAmountKnown`),
                  right.isProceedsAmountKnown === false ? warning(t(`${TENANCY}.amountWarning`)) : undefined
                )}
              </Grid>
              {right.isProceedsAmountKnown === false && hint(t(`${TENANCY}.amountHint`))}
              {right.isProceedsAmountKnown === true && (
                <>
                  <Grid size={HALF}>
                    <FormikDecimalTextField
                      id={fieldId("proceedsAmount")}
                      formik={formik}
                      path={path("proceedsAmount")}
                      label={t(`${TENANCY}.amount`)}
                      decimalScale={2}
                      minValue={0}
                      slotProps={{ input: { endAdornment: <InputAdornment position="end">€</InputAdornment> } }}
                    />
                  </Grid>
                  <Grid size={HALF}>
                    <FormikDatePicker formik={formik} path={path("proceedsDate")} label={t(`${TENANCY}.amountDate`)} />
                  </Grid>
                </>
              )}

              <Grid size={FULL}>{textArea("remarks", t(`${TENANCY}.remarks`))}</Grid>
            </>
          )}
        </Grid>
        <Button color="error" startIcon={<DeleteOutlineIcon />} onClick={onRemove} sx={{ alignSelf: "flex-end" }}>
          {t(`${TENANCY}.remove`, { number })}
        </Button>
      </Stack>
    </Paper>
  );
}
